import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import "./addStops.scss";
import { DataTable } from "../../../../../ui-components/data-table";
import getPlanProbillsColumns, {
    PLAN_PROBILLS_COLUMN_OPTIONS,
    PLAN_PROBILLS_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../../../plan-probills/planProbillsColumns";
import { DispatchPlanProbillsState } from "../../../../../slices/dispatch-planning/dispatchProbillsState";
import { useDispatch, useSelector } from "react-redux";
import { dispatchPlanProbillsSelector, getPlannableProbills } from "../../../../../slices/dispatch-planning/dispatchPlanProbillsSlice";
import { PlannableProbills } from "../../../../../app/data/plan-probills/models";
import XGSIcon from "../../../../../ui-components/icon/xgsIcon";
import XGSIcons from "../../../../../ui-components/icon/xgsIcons";
import { addProbillsToRouteResetState } from "../../../../../slices/dispatch-planning/routes/addProbillsToRouteSlice";
import { setStopsToAddToRoute } from "../../../../../slices/dispatch-planning/routes/stopsToAddToRouteSlice";
import AddStopsHeader from "./addStopsHeader";
import { CreateAppointment } from "../../../../appointment/createAppointment";

interface AddStopsProps {
    show: boolean;
    routeId: string;
    onClose?: any;
    terminalNumber: string
}
const AddStops: React.FC<AddStopsProps> = memo((props) => {
    const dispatch = useDispatch();
    const dispatchPlanProbillsState: DispatchPlanProbillsState = useSelector(
        dispatchPlanProbillsSelector
    );
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
        const hiddenSet = new Set(PLAN_PROBILLS_COLUMNS_HIDDEN_BY_DEFAULT);
        return PLAN_PROBILLS_COLUMN_OPTIONS.reduce(
            (acc, { id }) => ({ ...acc, [id]: !hiddenSet.has(id) }),
            {} as Record<string, boolean>
        );
    });
    const [columnOrder, setColumnOrder] = useState<string[]>(() =>
        PLAN_PROBILLS_COLUMN_OPTIONS.map(({ id }) => id)
    );
    const [appointmentProbillNumber, setAppointmentProbillNumber] = useState<number | null>(null);

    const onScheduleAppointment = useCallback((probillNumber: number) => {
        setAppointmentProbillNumber(probillNumber);
    }, []);

    const onColumnVisibilityChange = useCallback(
        (updater: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => {
            setColumnVisibility((prev) => (typeof updater === "function" ? updater(prev) : updater));
        },
        []
    );

    const selectedProbillsChanged = React.useCallback(
        (state: {
            selectedRowsData: PlannableProbills[];
        }) => {
            const selectedProbill: PlannableProbills[] = state.selectedRowsData.map(
                (selectedRow) => selectedRow
            );
            dispatch(setStopsToAddToRoute(selectedProbill));
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(getPlannableProbills(props.terminalNumber));
         // eslint-disable-next-line
    }, []);

    const terminalNumber = props.terminalNumber ? +props.terminalNumber : undefined;
    const columns = useMemo(
        () =>
            getPlanProbillsColumns({
                terminalNumber: terminalNumber ?? null,
                onScheduleAppointment,
            }),
        [terminalNumber, onScheduleAppointment]
    );

    const onClose = () => {
        setAppointmentProbillNumber(null);
        props.onClose(false);
        dispatch(addProbillsToRouteResetState());
    };
    return (
        <>
            {props.show &&
                <div className="add-stops-overlay">
                    <div className="add-stops-modal">
                        <div className="add-stops-modal-header">
                            <div className="add-stops-modal-header-titile">
                                Note: The selected probills may create multiple stops based on the address
                                <AddStopsHeader 
                                show= {props.show}
                                routeId= {props.routeId}
                                onClose = {props.onClose}
                                terminalNumber= {props.terminalNumber} />
                            </div>
                            <div
                                className="add-stops-modal-header-close"
                                onClick={onClose}
                            >
                                <XGSIcon
                                    icon={XGSIcons.faTimesCircle} />
                            </div>
                        </div>
                        <div className="add-stops-modal-table">
                        <DataTable
                            isLoading={dispatchPlanProbillsState.loading}
                            columns={columns}
                            data={dispatchPlanProbillsState.plannableProbills ?? []}
                            rowHeight={52}
                            ignoreCellClick="selection"
                            cursorPointer={true}
                            onSelectedRowsChange={selectedProbillsChanged}
                            responsive={true}
                            sorting={true}
                            enableMultiSort={true}
                            filters={true}
                            autoResetSelectedRows={true}
                            columnVisibility={columnVisibility}
                            onColumnVisibilityChange={onColumnVisibilityChange}
                            columnOrder={columnOrder}
                            onColumnOrderChange={setColumnOrder}
                            showColumnSelector={true}
                            columnSelectorOptions={PLAN_PROBILLS_COLUMN_OPTIONS}
                            enableColumnResize={true}
                            enableColumnReorder={true}
                            stickyHeaders={true}
                            noResultsText="No records to display"
                            enableRowSelection={true}
                            rowClickTogglesSelection={true}
                            keepTableOnLoading={true}
                        />
                        </div>
                    </div>
                </div>
            }
            {appointmentProbillNumber !== null && (
                <CreateAppointment
                    probillNumber={appointmentProbillNumber}
                    onClose={() => setAppointmentProbillNumber(null)}
                    stackAbovePageModals
                />
            )}
        </>
    );
});

export default AddStops;